import { useEffect } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { BrandLogo } from './BrandLogo'
import { Icon } from './Icon'
import {
  AUTH_ACCOUNT,
  AUTH_ACCOUNT_LINKS,
  AUTH_PATHS,
} from '../constants/auth'
import { CART_PATHS, CART_UI } from '../constants/cart'
import { LOCATION_MODAL } from '../constants/location'
import { NAV_CATEGORY_ALL_LABEL, TOPBAR } from '../constants/navigation'
import { SEARCH_UI } from '../constants/search'
import { WISHLIST_PATHS, WISHLIST_UI } from '../constants/wishlist'
import { isCheckoutRoute } from '../helpers/checkout'
import { cx, formatCurrency, getInitials } from '../helpers/format'
import { useAccountMenu } from '../hooks/useAccountMenu'
import { useAuth } from '../hooks/useAuth'
import { useCart } from '../hooks/useCart'
import { useDeliveryLocation } from '../hooks/useDeliveryLocation'
import { useHeaderAutoHide } from '../hooks/useHeaderAutoHide'
import { useHeaderSearch } from '../hooks/useHeaderSearch'
import { useNavCategories } from '../hooks/useNavCategories'
import { useWishlist } from '../hooks/useWishlist'
import '../styles/header.scss'

export function Header() {
  const {
    query,
    onQueryChange,
    onSearchFocus,
    mobileOpen,
    onSearchSubmit,
    toggleMobile,
    closeMobile,
    searchRef,
    panelOpen,
    suggestions,
    suggestionsLoading,
    suggestionsError,
    closePanel,
  } = useHeaderSearch()
  const { location, city, isModalOpen, locationLocked, openModal, closeModal } =
    useDeliveryLocation()
  const placeLabel = location?.label ?? LOCATION_MODAL.selectLabel
  const { categories } = useNavCategories()
  const { itemCount } = useCart()
  const { itemCount: wishlistCount } = useWishlist()
  const { isAuthenticated, displayName, logout } = useAuth()
  const initials = getInitials(displayName)
  const { open: accountOpen, menuRef, toggle: toggleAccount, close: closeAccount } =
    useAccountMenu()
  const { pathname } = useLocation()
  const lockLocation = locationLocked || isCheckoutRoute(pathname)
  const {
    hidden: headerHidden,
    headerRef,
    keepVisibleRef,
  } = useHeaderAutoHide(
    panelOpen || accountOpen || mobileOpen || isModalOpen,
  )

  useEffect(() => {
    if (lockLocation && isModalOpen) closeModal()
  }, [lockLocation, isModalOpen, closeModal])

  function closeCategoryMenu() {
    closeMobile()
    ;(document.activeElement as HTMLElement | null)?.blur()
  }

  return (
    <header
      ref={headerRef}
      className={cx('site-header', headerHidden && 'site-header--hidden')}
    >
      <div className="header-topbar">
        <div className="container header-topbar__inner">
          <p className="header-topbar__headline">
            <Icon name="flower" size={15} /> {TOPBAR.headline}
          </p>
          <p className="header-topbar__mid">
            <span>{TOPBAR.cities}</span>
            <span>
              <Icon name="building" size={14} /> {TOPBAR.gifting}
            </span>
          </p>
          <a href={TOPBAR.phoneHref} className="header-topbar__phone">
            <Icon name="phone" size={14} /> {TOPBAR.phoneDisplay}
          </a>
        </div>
      </div>

      <div className="header-main">
        <div
          className={cx(
            'container header-main__inner',
            lockLocation && 'header-main__inner--locked',
          )}
        >
          <button
            type="button"
            className="header-menu-btn"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            onClick={toggleMobile}
          >
            <span />
            <span />
            <span />
          </button>

          <BrandLogo onClick={closeMobile} />

          {lockLocation ? null : (
            <button
              type="button"
              className={cx('header-location', !location && 'header-location--empty')}
              onClick={openModal}
              aria-label={
                location
                  ? `${LOCATION_MODAL.changeAria}: ${placeLabel}`
                  : LOCATION_MODAL.changeAria
              }
            >
              <span className="header-location__icon">
                <Icon name="pin" size={17} strokeWidth={2} />
              </span>
              <span className="header-location__copy">
                <span className="header-location__eyebrow">{LOCATION_MODAL.deliverTo}</span>
                <span className="header-location__place">
                  <span className="header-location__full">{placeLabel}</span>
                  <span className="header-location__short">{city || placeLabel}</span>
                </span>
              </span>
              <Icon
                name="chevronDown"
                size={16}
                strokeWidth={2.2}
                className="header-location__chevron"
              />
            </button>
          )}

          <form
            className="header-search"
            onSubmit={onSearchSubmit}
            ref={searchRef}
            role="search"
          >
            <button
              type="submit"
              className="header-search__submit"
              aria-label={SEARCH_UI.submitAria}
            >
              <Icon name="search" size={19} strokeWidth={2} />
            </button>
            <label className="sr-only" htmlFor="marketplace-search">
              Search
            </label>
            <input
              id="marketplace-search"
              type="search"
              placeholder={SEARCH_UI.placeholder}
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              onFocus={onSearchFocus}
              autoComplete="off"
              aria-autocomplete="list"
              aria-expanded={panelOpen}
              aria-controls="header-search-suggestions"
            />
            {panelOpen ? (
              <div
                id="header-search-suggestions"
                className="header-search__panel"
                role="listbox"
                aria-label={SEARCH_UI.suggestionsLabel}
              >
                {suggestionsLoading ? (
                  <p className="header-search__status">{SEARCH_UI.searching}</p>
                ) : null}
                {suggestionsError ? (
                  <p className="header-search__status header-search__status--error">
                    {suggestionsError}
                  </p>
                ) : null}
                {!suggestionsLoading && !suggestionsError && suggestions.length === 0 ? (
                  <p className="header-search__status">{SEARCH_UI.emptyResults}</p>
                ) : null}
                {!suggestionsLoading && !suggestionsError
                  ? suggestions.map((product) => (
                      <Link
                        key={product.id}
                        to={product.to}
                        role="option"
                        className="header-search__suggestion"
                        onClick={closePanel}
                      >
                        {product.imageUrl ? (
                          <img src={product.imageUrl} alt="" />
                        ) : (
                          <span className="header-search__placeholder" aria-hidden />
                        )}
                        <span className="header-search__suggestion-copy">
                          <strong>{product.name}</strong>
                          <span>{formatCurrency(product.price)}</span>
                        </span>
                      </Link>
                    ))
                  : null}
                <button type="submit" className="header-search__all">
                  {SEARCH_UI.viewAllResults}
                </button>
              </div>
            ) : null}
          </form>

          <div className="header-actions">
            <Link
              to={WISHLIST_PATHS.page}
              className="header-action"
              aria-label={WISHLIST_UI.headerLabel}
            >
              <span className="header-action__icon">
                <Icon name="heart" size={21} strokeWidth={1.8} />
                {wishlistCount > 0 ? (
                  <span className="header-action__badge">{wishlistCount}</span>
                ) : null}
              </span>
              <span className="header-action__label">{WISHLIST_UI.headerLabel}</span>
            </Link>
            <Link to={CART_PATHS.page} className="header-action" aria-label={CART_UI.headerLabel}>
              <span className="header-action__icon">
                <Icon name="bag" size={21} strokeWidth={1.8} />
                {itemCount > 0 ? (
                  <span className="header-action__badge">{itemCount}</span>
                ) : null}
              </span>
              <span className="header-action__label">{CART_UI.headerLabel}</span>
            </Link>
            {isAuthenticated ? (
              <div className="header-account" ref={menuRef}>
                <button
                  type="button"
                  className={cx('header-action', accountOpen && 'is-open')}
                  aria-label={AUTH_ACCOUNT.menuAria}
                  aria-haspopup="menu"
                  aria-expanded={accountOpen}
                  aria-controls={AUTH_ACCOUNT.menuId}
                  onClick={toggleAccount}
                >
                  <span className="header-action__icon">
                    {initials ? (
                      <span className="header-action__avatar">{initials}</span>
                    ) : (
                      <Icon name="user" size={21} strokeWidth={1.8} />
                    )}
                  </span>
                  <span className="header-action__label">{AUTH_ACCOUNT.label}</span>
                </button>
                {accountOpen ? (
                  <div
                    id={AUTH_ACCOUNT.menuId}
                    className="header-account__menu"
                    role="menu"
                  >
                    <p className="header-account__identity">
                      <span>{AUTH_ACCOUNT.signedInAs}</span>
                      <strong>{displayName}</strong>
                    </p>
                    {AUTH_ACCOUNT_LINKS.map((item) => (
                      <Link
                        key={item.id}
                        to={item.to}
                        role="menuitem"
                        className="header-account__item"
                        onClick={closeAccount}
                      >
                        <Icon name={item.icon} size={16} />
                        {item.label}
                      </Link>
                    ))}
                    <button
                      type="button"
                      role="menuitem"
                      className="header-account__item header-account__item--logout"
                      onClick={() => {
                        closeAccount()
                        void logout()
                      }}
                    >
                      <Icon name={AUTH_ACCOUNT.logoutIcon} size={16} />
                      {AUTH_ACCOUNT.logout}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              <Link to={AUTH_PATHS.login} className="header-action" aria-label={AUTH_ACCOUNT.label}>
                <span className="header-action__icon">
                  <Icon name="user" size={21} strokeWidth={1.8} />
                </span>
                <span className="header-action__label">{AUTH_ACCOUNT.label}</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      <nav
        ref={keepVisibleRef}
        className={cx('header-cats', mobileOpen && 'is-open')}
        aria-label="Categories"
      >
        <ul className="container header-cats__list">
          {categories.map((item) => {
            const hasChildren = Boolean(item.children && item.children.length > 0)
            const childActive = hasChildren
              ? item.children!.some(
                  (child) =>
                    pathname === child.to || pathname.startsWith(`${child.to}/`),
                )
              : false
            const branchActive =
              pathname === item.to ||
              pathname.startsWith(`${item.to}/`) ||
              childActive

            return (
              <li
                key={item.id}
                className={cx('header-cats__item', hasChildren && 'has-menu')}
              >
                {hasChildren ? (
                  <span
                    className={cx(
                      'header-cats__link',
                      'header-cats__trigger',
                      item.emphasize && 'is-express',
                      branchActive && 'is-active',
                    )}
                    aria-haspopup="menu"
                  >
                    {item.icon ? (
                      <span className="header-cats__icon" aria-hidden>
                        <Icon name={item.icon} size={14} />
                      </span>
                    ) : null}
                    <span>{item.label}</span>
                  </span>
                ) : (
                  <NavLink
                    to={item.to}
                    end
                    onClick={closeMobile}
                    className={({ isActive }) =>
                      cx(
                        'header-cats__link',
                        item.emphasize && 'is-express',
                        isActive && 'is-active',
                      )
                    }
                  >
                    {item.icon ? (
                      <span className="header-cats__icon" aria-hidden>
                        <Icon name={item.icon} size={14} />
                      </span>
                    ) : null}
                    <span>{item.label}</span>
                  </NavLink>
                )}

                {hasChildren ? (
                  <ul className="header-cats__menu" role="menu">
                    <li role="none">
                      <NavLink
                        to={item.to}
                        end
                        role="menuitem"
                        onClick={closeCategoryMenu}
                        className={({ isActive }) =>
                          cx('header-cats__menu-link', isActive && 'is-active')
                        }
                      >
                        {NAV_CATEGORY_ALL_LABEL}
                      </NavLink>
                    </li>
                    {item.children!.map((child) => (
                      <li key={child.id} role="none">
                        <NavLink
                          to={child.to}
                          end
                          role="menuitem"
                          onClick={closeCategoryMenu}
                          className={({ isActive }) =>
                            cx('header-cats__menu-link', isActive && 'is-active')
                          }
                        >
                          {child.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            )
          })}
        </ul>
      </nav>
    </header>
  )
}
